import { useState, useEffect, useCallback } from "react";
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Select,
  Button,
  Banner,
  Spinner,
  TextContainer,
  Badge,
  BlockStack,
  InlineStack,
} from "@shopify/polaris";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getCampaign,
  createCampaign,
  updateCampaign,
  getGames,
  getRewardPolicies,
  createVariant,
  updateVariant,
  deleteVariant,
  createRewardPolicy,
  type Campaign,
  type Game,
  type RewardPolicy,
  type CampaignVariant,
} from "../api/campaigns";
import { RewardPolicyBuilder } from "../components/RewardPolicyBuilder/RewardPolicyBuilder";
import { VariantManager } from "../components/VariantManager/VariantManager";
import { RuleBuilder } from "../components/TargetingRuleBuilder";
import { validateTargetingRule } from "../components/TargetingRuleBuilder/validation";
import type { TargetingRuleV1 } from "../components/TargetingRuleBuilder";

function CampaignFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = id !== "new";
  
  // Get preselected game from URL query param (from Games Catalog "Use in Campaign" button)
  const preselectedGameId = searchParams.get("gameId");

  // Campaign form state
  const [name, setName] = useState("");
  const [placement, setPlacement] = useState("cart");
  const [status, setStatus] = useState<Campaign["status"]>("DRAFT");
  const [targetingRules, setTargetingRules] = useState<TargetingRuleV1 | null>(null);

  // Initialize targeting rules with default structure if null
  const safeTargetingRules: TargetingRuleV1 = targetingRules ?? {
    version: "1",
    rule: {
      type: "and",
      conditions: []
    }
  };

  // Data
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [rewardPolicies, setRewardPolicies] = useState<RewardPolicy[]>([]);
  const [variants, setVariants] = useState<CampaignVariant[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Reward policy builder state
  const [isCreatingPolicy, setIsCreatingPolicy] = useState(false);
  const [newPolicyName, setNewPolicyName] = useState("");
  const [newPolicyConfig, setNewPolicyConfig] = useState<any>(null);
  const [creatingPolicy, setCreatingPolicy] = useState(false);

  const loadCampaign = useCallback(async () => {
    if (!isEditMode || !id) return;

    try {
      setLoading(true);
      const data = await getCampaign(id);
      setCampaign(data);
      setName(data.name);
      setPlacement(data.placement);
      setStatus(data.status);
      setVariants(data.variants || []);
      // Load targeting rules (Phase 5.2)
      if (data.targetingRules && typeof data.targetingRules === "object") {
        setTargetingRules(data.targetingRules as TargetingRuleV1);
      } else {
        setTargetingRules(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load campaign");
    } finally {
      setLoading(false);
    }
  }, [isEditMode, id]);

  const loadData = useCallback(async () => {
    try {
      const [gamesData, policiesData] = await Promise.all([
        getGames(),
        getRewardPolicies(),
      ]);
      setGames(gamesData);
      setRewardPolicies(policiesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    }
  }, []);

  useEffect(() => {
    loadData();
    loadCampaign();
  }, [loadData, loadCampaign]);

  const handleSave = async () => {
    if (!name || !placement) {
      setError("Please fill in all required fields");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isEditMode && id) {
        await updateCampaign(id, { name, placement, status, targetingRules });
        setSuccessMessage("Campaign updated successfully");
      } else {
        const validStatus: "DRAFT" | "ACTIVE" | "PAUSED" = 
          status === "ENDED" ? "DRAFT" : status;
        const created = await createCampaign({ name, placement, status: validStatus, targetingRules });
        setSuccessMessage("Campaign created successfully");
        // Redirect to edit mode
        navigate(`/campaigns/${created.id}`, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  const handleAddVariant = async (variantData: {
    gameId: string;
    gameVersionId: string;
    rewardPolicyId: string;
    weight: number;
    config: any;
  }) => {
    if (!isEditMode || !id) {
      setError("Please save the campaign first before adding variants");
      return;
    }

    const variant = await createVariant(id, variantData);
    setVariants([...variants, variant]);
    setSuccessMessage("Variant added successfully");
    await loadCampaign(); // Reload to get full variant data
  };

  const handleUpdateVariant = async (
    variantId: string,
    updates: { weight?: number; config?: any; status?: "ACTIVE" | "DISABLED" }
  ) => {
    if (!isEditMode || !id) return;

    await updateVariant(id, variantId, updates);
    setSuccessMessage("Variant updated successfully");
    await loadCampaign();
  };

  const handleDeleteVariant = async (variantId: string) => {
    if (!isEditMode || !id) return;

    await deleteVariant(id, variantId);
    setVariants(variants.filter((v) => v.id !== variantId));
    setSuccessMessage("Variant deleted successfully");
  };

  const handleCreateRewardPolicy = async () => {
    if (!newPolicyName || !newPolicyConfig) {
      setError("Please provide a name and configure the policy");
      return;
    }

    setCreatingPolicy(true);
    setError(null);

    try {
      const policy = await createRewardPolicy({
        name: newPolicyName,
        policy: newPolicyConfig,
        expectedValue: 0,
      });
      setRewardPolicies([...rewardPolicies, policy]);
      setSuccessMessage("Reward policy created successfully");
      setIsCreatingPolicy(false);
      setNewPolicyName("");
      setNewPolicyConfig(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create reward policy");
    } finally {
      setCreatingPolicy(false);
    }
  };

  if (loading) {
    return (
      <Page title={isEditMode ? "Edit Campaign" : "Create Campaign"}>
        <Card>
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <Spinner size="large" />
          </div>
        </Card>
      </Page>
    );
  }

  const sessionCount = campaign?._count?.sessions || 0;

  return (
    <Page
      title={isEditMode ? "Edit Campaign" : "Create Campaign"}
      backAction={{ content: "Campaigns", url: "/campaigns" }}
      primaryAction={{
        content: saving ? "Saving..." : "Save",
        onAction: handleSave,
        loading: saving,
      }}
      secondaryActions={[
        {
          content: "Cancel",
          onAction: () => navigate("/campaigns"),
        },
      ]}
    >
      {error && (
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      {successMessage && (
        <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Banner>
      )}

      <Layout>
        <Layout.Section>
          <Card>
            <div style={{ padding: "1rem" }}>
              <p style={{ fontWeight: 600, marginBottom: "1rem" }}>Campaign Details</p>
              <FormLayout>
                <TextField
                  label="Campaign Name"
                  value={name}
                  onChange={setName}
                  placeholder="e.g., Cart Page Wheel"
                  autoComplete="off"
                  requiredIndicator
                />

                <Select
                  label="Placement"
                  value={placement}
                  options={[
                    { label: "Cart Page", value: "cart" },
                    { label: "Product Page", value: "product" },
                    { label: "Checkout", value: "checkout" },
                    { label: "Thank You Page", value: "thankyou" },
                    { label: "Homepage", value: "homepage" },
                  ]}
                  onChange={setPlacement}
                  requiredIndicator
                />

                <Select
                  label="Status"
                  value={status}
                  options={[
                    { label: "Draft", value: "DRAFT" },
                    { label: "Active", value: "ACTIVE" },
                    { label: "Paused", value: "PAUSED" },
                    { label: "Ended", value: "ENDED" },
                  ]}
                  onChange={(value) => setStatus(value as Campaign["status"])}
                />
              </FormLayout>
            </div>
          </Card>

          {isEditMode && sessionCount > 0 && (
            <Card>
              <div style={{ padding: "1rem" }}>
                <BlockStack gap="200">
                  <h3 className="Polaris-Text--headingMd">Analytics</h3>
                  <TextContainer>
                    <p>
                      This campaign has <strong>{sessionCount}</strong> total{" "}
                      {sessionCount === 1 ? "session" : "sessions"}.
                    </p>
                  </TextContainer>
                </BlockStack>
              </div>
            </Card>
          )}
        </Layout.Section>

        <Layout.Section>
          <Card>
            <div style={{ padding: "1rem" }}>
              <BlockStack gap="200">
                <h3 className="Polaris-Text--headingMd">Quick Tips</h3>
                <TextContainer>
                  <ul>
                    <li>Choose a descriptive name for your campaign</li>
                    <li>Select where the game should appear on your storefront</li>
                    <li>
                      Start with "Draft" status to test before activating
                    </li>
                    <li>Add at least one variant to launch the campaign</li>
                  </ul>
                </TextContainer>
              </BlockStack>
            </div>
          </Card>

          {isEditMode && (
            <Card>
              <div style={{ padding: "1rem" }}>
                <BlockStack gap="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <h3 className="Polaris-Text--headingMd">Status</h3>
                    {status === "ACTIVE" ? (
                      <Badge tone="success">Active</Badge>
                    ) : status === "PAUSED" ? (
                      <Badge tone="warning">Paused</Badge>
                    ) : status === "DRAFT" ? (
                      <Badge>Draft</Badge>
                    ) : (
                      <Badge tone="info">Ended</Badge>
                    )}
                  </InlineStack>
                  <TextContainer>
                    <p style={{ fontSize: "0.875rem", color: "#6d7175" }}>
                      {status === "ACTIVE" && "This campaign is live and visible to customers"}
                      {status === "PAUSED" && "This campaign is paused and not visible to customers"}
                      {status === "DRAFT" && "This campaign is in draft mode and not visible to customers"}
                      {status === "ENDED" && "This campaign has ended and is no longer visible"}
                    </p>
                  </TextContainer>
                </BlockStack>
              </div>
            </Card>
          )}
        </Layout.Section>

        {isEditMode && (
          <>
            <Layout.Section>
              <VariantManager
                campaignId={id!}
                variants={variants}
                games={games}
                rewardPolicies={rewardPolicies}
                onAddVariant={handleAddVariant}
                onUpdateVariant={handleUpdateVariant}
                onDeleteVariant={handleDeleteVariant}
              />
            </Layout.Section>

            <Layout.Section>
              <Card>
                <div style={{ padding: "1rem" }}>
                  <p style={{ fontWeight: 600, marginBottom: "1rem" }}>Reward Policies</p>
                  <TextContainer>
                    <p>
                      Reward policies define what rewards customers can win. You can create
                      new policies here or use existing ones when adding variants.
                    </p>
                  </TextContainer>

                  <div style={{ marginTop: "1rem" }}>
                    {rewardPolicies.length > 0 ? (
                      <BlockStack gap="200">
                        {rewardPolicies.map((policy) => (
                          <Card key={policy.id}>
                            <div style={{ padding: "1rem" }}>
                            <InlineStack align="space-between">
                              <strong>{policy.name}</strong>
                              <Badge>
                                {`${JSON.parse(JSON.stringify(policy.policy)).outcomes?.length || 0} outcomes`}
                              </Badge>
                            </InlineStack>
                            </div>
                          </Card>
                        ))}
                      </BlockStack>
                    ) : (
                      <Banner>
                        No reward policies found. Create one below to get started.
                      </Banner>
                    )}
                  </div>

                  <div style={{ marginTop: "1.5rem" }}>
                    {!isCreatingPolicy ? (
                      <Button onClick={() => setIsCreatingPolicy(true)}>
                        Create New Reward Policy
                      </Button>
                    ) : (
                      <Card>
                        <div style={{ padding: "1rem" }}>
                        <FormLayout>
                          <TextField
                            label="Policy Name"
                            value={newPolicyName}
                            onChange={setNewPolicyName}
                            placeholder="e.g., Standard Rewards"
                            autoComplete="off"
                          />

                          <RewardPolicyBuilder
                            onChange={setNewPolicyConfig}
                          />

                          <InlineStack gap="200">
                            <Button
                              onClick={handleCreateRewardPolicy}
                              variant="primary"
                              loading={creatingPolicy}
                            >
                              Create Policy
                            </Button>
                            <Button onClick={() => setIsCreatingPolicy(false)}>
                              Cancel
                            </Button>
                          </InlineStack>
                        </FormLayout>
                        </div>
                      </Card>
                    )}
                  </div>
                </div>
              </Card>
            </Layout.Section>
          </>
        )}

        {!isEditMode && (
          <Layout.Section>
            {preselectedGameId && games.find(g => g.id === preselectedGameId) ? (
              <Banner tone="info">
                <p>
                  <strong>Selected game: {games.find(g => g.id === preselectedGameId)?.name}</strong>
                </p>
                <p style={{ marginTop: "0.5rem" }}>
                  Save the campaign first, then you can add a variant using this game.
                </p>
              </Banner>
            ) : (
              <Banner>
                Save the campaign first to add variants and configure rewards.
              </Banner>
            )}
          </Layout.Section>
        )}

        {/* Phase 5.2: Targeting Rules */}
        <Layout.Section>
          <RuleBuilder
            rule={safeTargetingRules}
            onChange={setTargetingRules}
            onValidate={validateTargetingRule}
            showPreview={true}
          />
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default CampaignFormPage;
